"use client";

import type { ReactNode } from "react";

import { Icon } from "@/components/ui/icon";
import { SocialNetworkIcon } from "@/components/ui/social-network-icon";
import { TooltipMarked } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import type { SocialNetwork } from "@/features/candidate/lib/socials";

export interface AccountHeaderProps {
  network: SocialNetwork;
  username: string;
  externalUrl: string;
  tooltip: ReactNode;
  meta: ReactNode;
}

/**
 * Shared header row used at the top of each technical-profile sub-block
 * (GitHub, Stack Overflow). Renders the platform logo on the left, the
 * username + tooltip "i" + meta lines in the middle, and an external-link
 * affordance in the top-right corner.
 */
export function AccountHeader({
  network,
  username,
  externalUrl,
  tooltip,
  meta,
}: AccountHeaderProps) {
  return (
    <div className="flex w-full min-w-0 items-start gap-3">
      <div
        className={cn(
          "flex size-10 shrink-0 items-center justify-center rounded-md border border-border bg-surface-elevated",
          // Slight inner shadow to match the screenshot's elevated tile.
          "shadow-[inset_0px_-1px_0px_0px_rgba(0,0,0,0.04)]",
        )}
      >
        <SocialNetworkIcon
          network={network}
          className="size-5"
          aria-hidden
        />
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex min-w-0 items-center gap-1.5">
          <span className="truncate text-sm font-medium leading-5 text-foreground">
            {username}
          </span>
          <TooltipMarked
            iconClassName="size-3.5 text-foreground/40"
            iconStyle="solid"
          >
            {tooltip}
          </TooltipMarked>
        </div>
        <div className="flex flex-col gap-0.5 text-[13px] font-normal leading-[18px] tracking-[-0.13px] text-foreground/60">
          {meta}
        </div>
      </div>
      <a
        href={externalUrl}
        target="_blank"
        rel="noreferrer"
        aria-label={`Open ${username} on ${network}`}
        className="shrink-0 rounded-md p-1 text-foreground/40 transition-colors hover:text-foreground/70"
      >
        <Icon name="IconArrowUpRight" size={16} />
      </a>
    </div>
  );
}
